import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Plus } from '@untitledui/icons/Plus';
import { Trash01 } from '@untitledui/icons/Trash01';
import { useState } from 'react';

import { AlertFloating } from '@/components/application/alerts/alerts';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { ConfirmationModal } from '@/components/base/modals/confirmation-modal';
import { ControlledModal } from '@/components/base/modals/modal';
import { notify } from '@/components/base/notifications/notifications';
import { queryKeys } from '@/hooks/api/query-keys';
import { SSO_CLAIMED_DOMAIN_REGEX } from '@common/utils';

import type { SsoDomain } from '../-utils/organization-settings-server';

import { ssoDomainsQueryOptions } from '../-utils/organization-settings-query';
import {
  addSsoDomain,
  deleteSsoDomain,
  setSsoDomainEnabled,
} from '../-utils/organization-settings-server';
import { SettingsSection } from '../../settings/-components/settings-section';

export const Route = createFileRoute(
  '/researchers/_authenticated/organization-settings/organization/sso-domains',
)({
  component: SsoDomainsPage,
  head: () => ({ meta: [{ title: t`SSO domains` }] }),
});

type Pending =
  | { kind: 'toggle'; domain: SsoDomain }
  | { kind: 'delete'; domain: SsoDomain };

function SsoDomainsPage() {
  const { activeDashboard } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const domains = useQuery(
    ssoDomainsQueryOptions(activeDashboard.organization ?? ''),
  );
  const [pending, setPending] = useState<Pending | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.workspaceSettings.all(),
    });

  const act = useMutation({
    mutationFn: async (action: Pending) => {
      const domainId = action.domain.id;
      const result =
        action.kind === 'toggle'
          ? await setSsoDomainEnabled({
              data: { domainId, verified: !action.domain.verified },
            })
          : await deleteSsoDomain({ data: { domainId } });
      if (!result.success) {
        throw new Error(result.message ?? t`Please try again.`);
      }
      return action;
    },
    onSuccess: async (action) => {
      setPending(null);
      notify({
        title:
          action.kind === 'delete'
            ? t`Domain deleted`
            : action.domain.verified
              ? t`Domain disabled`
              : t`Domain enabled`,
        color: 'success',
      });
      await refresh();
    },
    onError: (error) =>
      notify({
        title: t`Couldn't update the domain`,
        description: error.message,
        color: 'error',
      }),
  });

  return (
    <SettingsSection
      title={t`SSO domains`}
      description={t`Email domains whose users sign in through your organization's SSO provider.`}
    >
      {domains.isError ? (
        <AlertFloating
          color="error"
          title={t`We couldn't load SSO domains`}
          description={t`Refresh the page to try again.`}
          confirmLabel=""
        />
      ) : domains.isPending ? (
        <div className="h-32 animate-pulse rounded-lg bg-secondary" />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex justify-end">
            <Button
              color="secondary"
              size="sm"
              iconLeading={Plus}
              onClick={() => setIsAddOpen(true)}
            >
              <Trans>Add domain</Trans>
            </Button>
          </div>
          {domains.data.length === 0 ? (
            <p className="py-6 text-center text-sm text-tertiary">
              <Trans>No SSO domains yet.</Trans>
            </p>
          ) : (
            <ul className="divide-y divide-secondary">
              {domains.data.map((domain) => (
                <li key={domain.id} className="flex items-center gap-3 py-3">
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <p className="truncate text-sm font-semibold text-primary">
                      {domain.domain}
                    </p>
                    {domain.verified && (
                      <Badge type="pill-color" color="success" size="sm">
                        <Trans>Enabled</Trans>
                      </Badge>
                    )}
                  </div>
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={() => setPending({ kind: 'toggle', domain })}
                  >
                    {domain.verified ? (
                      <Trans>Disable</Trans>
                    ) : (
                      <Trans>Enable</Trans>
                    )}
                  </Button>
                  <Button
                    color="tertiary"
                    size="sm"
                    iconLeading={Trash01}
                    aria-label={t`Delete ${domain.domain}`}
                    onClick={() => setPending({ kind: 'delete', domain })}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {isAddOpen && (
        <AddDomainModal
          existing={domains.data?.map((d) => d.domain) ?? []}
          onClose={() => setIsAddOpen(false)}
          onAdded={() => void refresh()}
        />
      )}

      <ConfirmationModal
        isOpen={pending !== null}
        onOpenChange={(open) => !open && setPending(null)}
        title={
          pending?.kind === 'delete'
            ? t`Delete ${pending.domain.domain}?`
            : pending?.domain.verified
              ? t`Disable ${pending.domain.domain}?`
              : t`Enable ${pending?.domain.domain ?? ''}?`
        }
        description={t`This changes how everyone with an email address at this domain signs in.`}
        confirmLabel={
          pending?.kind === 'delete'
            ? t`Delete domain`
            : pending?.domain.verified
              ? t`Disable`
              : t`Enable`
        }
        isDestructive={pending?.kind === 'delete'}
        isLoading={act.isPending}
        onConfirm={() => pending && act.mutate(pending)}
        onCancel={() => setPending(null)}
      />
    </SettingsSection>
  );
}

function AddDomainModal({
  existing,
  onClose,
  onAdded,
}: {
  existing: string[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const [domain, setDomain] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const value = domain.trim().toLowerCase();
  const error = !submitted
    ? undefined
    : !SSO_CLAIMED_DOMAIN_REGEX.test(value)
      ? t`Enter a domain like acme.com`
      : existing.includes(value)
        ? t`This domain already exists`
        : undefined;

  const add = useMutation({
    mutationFn: async () => {
      const result = await addSsoDomain({ data: { domain: value } });
      if (!result.success) {
        throw new Error(result.message ?? t`Please try again.`);
      }
    },
    onSuccess: () => {
      notify({ title: t`Domain added`, color: 'success' });
      onAdded();
      onClose();
    },
    onError: (err) =>
      notify({
        title: t`Couldn't add the domain`,
        description: err.message,
        color: 'error',
      }),
  });

  return (
    <ControlledModal
      isOpen
      onOpenChange={(open) => !open && onClose()}
      centered
      title={t`Add domain`}
      className="mx-auto w-full max-w-md rounded-xl bg-primary p-6 shadow-xl"
    >
      <form
        className="flex w-full flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          if (
            SSO_CLAIMED_DOMAIN_REGEX.test(value) &&
            !existing.includes(value)
          ) {
            add.mutate();
          }
        }}
      >
        <h2 className="text-lg font-semibold text-primary">
          <Trans>Add domain</Trans>
        </h2>
        <Input
          label={t`Domain`}
          placeholder="acme.com"
          value={domain}
          onChange={setDomain}
          isInvalid={!!error}
          hint={error}
          size="xs"
        />
        <div className="flex justify-end gap-3">
          <Button color="secondary" size="md" onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            type="submit"
            color="primary"
            size="md"
            isLoading={add.isPending}
          >
            <Trans>Add domain</Trans>
          </Button>
        </div>
      </form>
    </ControlledModal>
  );
}
